var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { parse } from 'query-string';
import * as React from "react";
import { NavLink } from "react-router-dom";
import { Button, Checkbox, Dimmer, Form, Header, Loader, Message, Select, } from "semantic-ui-react";
import { AppPaths, ShopLink } from "./AppConfig";
import { ORIGIN } from "./Config";
import ErrorDisplay from "./ErrorDisplay";
import BaseFieldWithErrorDisplay from "./FieldWithErrorDisplay";
import { RelevantCatalogType, } from "./kit/automated-pro/RnaCatalog";
import { MetaCatalogLoader } from "./kit/Catalog";
import { LoadWrapper } from "./kit/rna-fish/LoadWrapper";
import { CatalogMode, postJsonNoAuthn, postJsonWithAuthn } from "./RequestHelpers";
var VERIFY_EMAIL_ENDPOINT = "/user/verify-email";
var CREATE_STARTER_KIT_ENDPOINT = "/user/create-starter-kit";
var STARTER_KIT_DEFAULT_AMPLIFIER_SYSTEM = "X2";
var HCR_STARTER_KIT_DEFAULT_PROTOCOL_TYPE_ID = "ROCHE";
var HCR_STARTER_KIT_DEFAULT_ORGANISM_NAME = "Mouse";
var HCR_STARTER_KIT_DEFAULT_ENZYME_LABEL = "AP";
var HCR_STARTER_KIT_SAMPLE_TYPE_LABEL = "Tissue section";
var HCR_STARTER_KIT_PROTOCOL_TYPE_ORDER = [
    "ROCHE",
    "LEICA",
    "ONCORE_PRO_X",
    "BOND_III",
];
var HCR_STARTER_KIT_ORGANISM_NAMES = ["Mouse", "Human"];
var HCR_STARTER_KIT_ENZYME_LABELS = ["AP", "HRP"];
var HCR_STARTER_KIT_SAMPLE_TYPE_OPTIONS = [
    { text: HCR_STARTER_KIT_SAMPLE_TYPE_LABEL, value: HCR_STARTER_KIT_SAMPLE_TYPE_LABEL },
];
var FieldWithErrorDisplay = function (props) {
    return React.createElement(BaseFieldWithErrorDisplay, __assign({}, props, { inlineErrors: true }));
};
var STARTER_KIT_OPTIONS = [
    {
        value: "GOLD_RNA_FISH",
        title: "HCR™ Gold RNA-FISH",
        description: "Multiplex, quantitative RNA imaging without enzymes"
    },
    {
        value: "PRO_RNA_ISH",
        title: "HCR™ Pro RNA-ISH",
        description: "Extreme-sensitivity chromogenic RNA imaging"
    },
    {
        value: "GOLD_IF",
        title: "HCR™ Gold IF",
        description: "Multiplex protein imaging with your primary antibodies"
    },
];
var MANUAL_WORKFLOW_OPTIONS = [
    { text: "Manual workflow", value: "MANUAL" },
];
var STARTER_KIT_SAMPLE_TYPE_OPTIONS = [
    { text: "Whole-Mount", value: "WHOLE_MOUNT" },
    { text: "Tissue Section", value: "TISSUE_SECTION" },
    { text: "Cells on a Slide", value: "CELLS_SLIDE" },
    { text: "Cells in Suspension", value: "CELLS_SUSPENSION" },
];
var RNA_FISH_STARTER_KIT_SAMPLE_TYPE_OPTIONS = STARTER_KIT_SAMPLE_TYPE_OPTIONS.concat([
    { text: "Northern Blot", value: "NORTHERN_BLOT" },
]);
var STARTER_KIT_ENCODER_SPECIES_OPTIONS = [
    { text: "Mouse IgG1", value: "MOUSE_IGG1" },
    { text: "Rabbit IgG", value: "RABBIT_IGG" },
];
var STARTER_KIT_DETAILS = {
    GOLD_RNA_FISH: {
        title: "HCR™ Gold RNA-FISH Starter Kit",
        sampleCount: "About 25 slides or 10 whole-mount samples",
        items: [
            {
                category: "Probe",
                title: "HCR™ Sample Readiness Probe",
                description: "Universal positive control for sample readiness",
            },
            {
                category: "Amplifier",
                title: "Three HCR™ Gold amplifier systems",
                description: "X1-488, X2-546, and X3-647; up to three colors",
            },
            {
                category: "Buffers",
                title: "HCR™ Gold buffers",
                description: "Hybridization, wash, and amplification buffers",
            },
        ],
        note: "Custom HCR™ HiFi Probes for your RNA targets are available now or later.",
    },
    PRO_RNA_ISH: {
        title: "HCR™ Pro RNA-ISH Starter Kit",
        sampleCount: "About 10 slides or 4 whole-mount samples",
        items: [
            {
                category: "Probe",
                title: "HCR™ Sample Readiness Probe",
                description: "Universal positive control for sample readiness",
            },
            {
                category: "Amplifier",
                title: "HCR™ Pro Amplifier kit",
                description: "Enzymatic amplification for demanding targets",
            },
            {
                category: "Chromogen",
                title: "Matisse chromogen kit",
                description: "Brightfield imaging and archival staining",
            },
            {
                category: "Buffers",
                title: "HCR™ Pro buffers",
                description: "Hybridization, probe wash, and amplifier wash buffers",
            },
        ],
        note: "Custom HCR™ HiFi Probes for your RNA targets are available now or later.",
    },
    GOLD_IF: {
        title: "HCR™ Gold IF Starter Kit",
        sampleCount: "About 25 slides or 10 whole-mount samples",
        items: [
            {
                category: "Encoder",
                title: "Two HCR™ HiFi Encoders",
                description: "Matched to primary antibody species and isotype",
            },
            {
                category: "Amplifier",
                title: "Two HCR™ Gold amplifiers",
                description: "546 and 647; up to two colors",
            },
            {
                category: "Control",
                title: "Matching isotype control",
                description: "Assesses nonspecific antibody binding",
            },
            {
                category: "Buffers",
                title: "HCR™ Gold buffers",
                description: "Antibody, amplifier, and wash buffers",
            },
        ],
        note: "Use your own trusted primary antibodies.",
    },
};
var VerifyEmailPage = /** @class */ (function (_super) {
    __extends(VerifyEmailPage, _super);
    function VerifyEmailPage(props) {
        var _this = _super.call(this, props) || this;
        _this.renderSuccessMessage = function () {
            if (_this.shouldShowStarterKitRequest()) {
                return React.createElement(Message, { success: true },
                    React.createElement(Message.Header, null, "Email verified"),
                    "Choose a Starter Kit below, or continue to the store.");
            }
            return React.createElement(Message, { success: true },
                React.createElement(Message.Header, null, "Email verified"),
                "You may now ",
                React.createElement(NavLink, { to: '/user/log-in' }, "log in"),
                ".");
        };
        _this.renderStarterKitStep = function () {
            var selectedProduct = _this.selectedStarterKitProduct();
            var details = selectedProduct === "" ? undefined : STARTER_KIT_DETAILS[selectedProduct];
            var includedGridClass = details !== undefined ? "starter-kit-included-grid columns-" + details.items.length : "";
            var isGoldIf = selectedProduct === "GOLD_IF";
            var sampleTypeOptions = _this.starterKitSampleTypeOptions();
            return React.createElement(Form, { error: _this.state.starterKitErrors.length > 0 },
                React.createElement(Header, { as: "h4", className: "new-registration-section-title starter-kit-main-title" }, "Choose your HCR\u2122 imaging product"),
                React.createElement("p", { className: "new-registration-hint" }, "Your complimentary Starter Kit will be tailored to your sample and workflow."),
                React.createElement("div", { className: "starter-kit-product-label" }, "Product"),
                React.createElement("div", { className: "starter-kit-options" }, STARTER_KIT_OPTIONS.map(function (opt) {
                    var selected = _this.state.starterKitProduct === opt.value;
                    return React.createElement("div", { key: opt.value, className: "starter-kit-option " + (selected ? "selected" : ""), onClick: _this.onSelectStarterKitProduct(opt.value) },
                        React.createElement(Checkbox, { radio: true, checked: selected, onChange: _this.onSelectStarterKitProduct(opt.value) }),
                        React.createElement("div", null,
                            React.createElement("div", { className: "starter-kit-option-title" }, opt.title),
                            React.createElement("div", { className: "starter-kit-option-description" }, opt.description)));
                })),
                details !== undefined &&
                    React.createElement("div", { className: "starter-kit-section-card starter-kit-included-card" },
                        React.createElement(Header, { as: "h4", className: "new-registration-section-title" }, "What's included"),
                        React.createElement("div", { className: "starter-kit-included-kit-title" }, details.title),
                        React.createElement("div", { className: "starter-kit-included-sample-count" }, details.sampleCount),
                        React.createElement("div", { className: includedGridClass }, details.items.map(function (item) {
                            return React.createElement("div", { key: item.category, className: "starter-kit-included-item" },
                                React.createElement("div", { className: "starter-kit-included-category" }, item.category),
                                React.createElement("div", { className: "starter-kit-included-item-title" }, item.title),
                                React.createElement("div", { className: "starter-kit-included-item-description" }, item.description));
                        })),
                        React.createElement("div", { className: "starter-kit-included-note" }, details.note)),
                selectedProduct !== "" &&
                    React.createElement("div", { className: "starter-kit-section-card starter-kit-config-card" },
                        React.createElement(Header, { as: "h4", className: "new-registration-section-title" }, "Configure your kit"),
                        React.createElement("div", { className: "starter-kit-config-grid " + (isGoldIf ? "has-encoder-field" : "") },
                            React.createElement(Form.Field, null,
                                React.createElement("label", null, "Platform"),
                                React.createElement(Select, { disabled: true, options: MANUAL_WORKFLOW_OPTIONS, value: "MANUAL" })),
                            React.createElement(FieldWithErrorDisplay, { errors: _this.state.starterKitErrors, errorPath: "/starterKit/sampleType", labelContent: "Sample type", required: true },
                                React.createElement(Select, { placeholder: "Select...", options: sampleTypeOptions, value: _this.state.starterKitSampleType, onChange: _this.onStarterKitSelectChange("starterKitSampleType") })),
                            isGoldIf &&
                                React.createElement(FieldWithErrorDisplay, { errors: _this.state.starterKitErrors, errorPath: "/starterKit/encoderSpecies", labelContent: "For encoding isotype", required: true },
                                    React.createElement(Select, { placeholder: "Select...", options: STARTER_KIT_ENCODER_SPECIES_OPTIONS, value: _this.state.starterKitEncoderSpecies, onChange: _this.onStarterKitSelectChange("starterKitEncoderSpecies") })))),
                React.createElement("p", { className: "starter-kit-skip-copy" },
                    "Not requesting a Starter Kit?",
                    " ",
                    React.createElement(ShopLink, { to: AppPaths.LOCAL_NEW_KIT }, "Continue to the store.")),
                _this.state.starterKitRequested &&
                    React.createElement(Message, { positive: true }, "Starter Kit request received. We added it to your cart."),
                React.createElement(ErrorDisplay, { errors: _this.state.starterKitErrors, errorPath: "/" }));
        };
        _this.renderHcrAutomatedStarterKitRequest = function () {
            return React.createElement(LoadWrapper, { loader: new MetaCatalogLoader(CatalogMode.Consumer, RelevantCatalogType.Initial), render: _this.renderHcrAutomatedStarterKitStep });
        };
        _this.renderHcrAutomatedStarterKitStep = function (catalog) {
            var protocolTypes = _this.hcrAutomatedStarterKitProtocolTypes(catalog);
            var selectedProtocolType = _this.selectedAutomatedProProtocolType(catalog);
            var organisms = _this.hcrAutomatedStarterKitOrganisms(catalog);
            var selectedOrganism = _this.selectedAutomatedProOrganism(catalog);
            var enzymes = selectedProtocolType === undefined
                ? []
                : _this.hcrAutomatedStarterKitEnzymes(catalog, selectedProtocolType);
            var selectedEnzyme = selectedProtocolType === undefined
                ? undefined
                : _this.selectedAutomatedProEnzyme(catalog, selectedProtocolType);
            var protocolTypeOptions = protocolTypes.map(function (protocolType) { return ({
                key: protocolType.id,
                text: _this.hcrAutomatedStarterKitProtocolTypeLabel(protocolType),
                value: protocolType.id,
            }); });
            var organismOptions = organisms.map(function (organism) { return ({
                key: organism.id,
                text: organism.name,
                value: organism.id,
            }); });
            var enzymeOptions = enzymes.map(function (enzyme) { return ({
                key: enzyme.id,
                text: _this.hcrAutomatedStarterKitEnzymeLabel(enzyme),
                value: enzyme.id,
            }); });
            return React.createElement(Form, { error: _this.state.starterKitErrors.length > 0, className: "hcr-automated-starter-kit-form" },
                React.createElement(Header, { as: "h3", className: "hcr-automated-starter-kit-heading" }, "1-plex HCR\u2122 Pro RNA-ISH"),
                React.createElement("p", { className: "hcr-automated-starter-kit-copy" }, "We'll send you a complimentary Starter Kit to try HCR\u2122 Pro RNA-ISH on tissue sections in your lab."),
                React.createElement("div", { className: "starter-kit-section-card hcr-automated-included-card" },
                    React.createElement(Header, { as: "h4", className: "new-registration-section-title" }, "What's included"),
                    React.createElement("div", { className: "hcr-automated-sample-count" }, "About 20 slides"),
                    React.createElement("div", { className: "hcr-automated-included-grid" },
                        React.createElement("div", { className: "starter-kit-included-item" },
                            React.createElement("div", { className: "starter-kit-included-category" }, "Probe"),
                            React.createElement("div", { className: "starter-kit-included-item-title" }, "Positive and negative control HCR\u2122 HiFi Probes"),
                            React.createElement("div", { className: "starter-kit-included-item-description" }, "PPIB/Ppib and dapB targets")),
                        React.createElement("div", { className: "starter-kit-included-item" },
                            React.createElement("div", { className: "starter-kit-included-category" }, "Reagents"),
                            React.createElement("div", { className: "starter-kit-included-item-title" }, "HCR\u2122 Pro RNA-ISH Detection Kit"),
                            React.createElement("div", { className: "starter-kit-included-item-description" }, "Matched to your platform and detection enzyme"))),
                    React.createElement("div", { className: "starter-kit-included-note" }, "Custom HCR\u2122 HiFi Probes are available separately. Platform consumables are not included.")),
                React.createElement("div", { className: "starter-kit-section-card hcr-automated-kit-details-card" },
                    React.createElement(Header, { as: "h4", className: "new-registration-section-title" }, "Kit details"),
                    React.createElement("div", { className: "hcr-automated-kit-details-grid" },
                        React.createElement(FieldWithErrorDisplay, { errors: _this.state.starterKitErrors, errorPath: "/starterKit/protocolTypeId", labelContent: "Platform", required: true },
                            React.createElement(Select, { placeholder: "Select...", options: protocolTypeOptions, value: selectedProtocolType === undefined ? "" : selectedProtocolType.id, onChange: _this.onAutomatedProProtocolTypeChange(catalog) })),
                        React.createElement(Form.Field, null,
                            React.createElement("label", null, "Sample type"),
                            React.createElement(Select, { disabled: true, options: HCR_STARTER_KIT_SAMPLE_TYPE_OPTIONS, value: HCR_STARTER_KIT_SAMPLE_TYPE_LABEL })),
                        React.createElement(FieldWithErrorDisplay, { errors: _this.state.starterKitErrors, errorPath: "/starterKit/libraryOrganismId", labelContent: "Organism", required: true },
                            React.createElement(Select, { placeholder: "Select...", options: organismOptions, value: selectedOrganism === undefined ? "" : selectedOrganism.id, onChange: _this.onAutomatedProLibraryOrganismChange })),
                        React.createElement(FieldWithErrorDisplay, { errors: _this.state.starterKitErrors, errorPath: "/starterKit/enzymeCompatibilityId", labelContent: "Detection enzyme", required: true },
                            React.createElement(Select, { placeholder: "Select...", options: enzymeOptions, value: selectedEnzyme === undefined ? "" : selectedEnzyme.id, onChange: _this.onAutomatedProEnzymeCompatibilityChange })))),
                React.createElement("p", { className: "starter-kit-skip-copy" },
                    "Not requesting a Starter Kit?",
                    " ",
                    React.createElement(ShopLink, { to: AppPaths.LOCAL_NEW_KIT }, "Continue to the store.")),
                _this.state.starterKitRequested &&
                    React.createElement(Message, { positive: true }, "Starter Kit request received. We added it to your cart."),
                React.createElement(ErrorDisplay, { errors: _this.state.starterKitErrors, errorPath: "/" }),
                React.createElement("div", { className: "new-registration-actions hcr-automated-starter-kit-actions" },
                    React.createElement("span", null),
                    React.createElement(Button, { positive: true, content: "Request your complimentary Starter Kit", disabled: !_this.canRequestAutomatedProStarterKit(catalog), onClick: _this.onRequestAutomatedProStarterKit(catalog) })));
        };
        _this.renderStarterKitActions = function () {
            return React.createElement("div", { className: "new-registration-actions" },
                React.createElement("span", null),
                React.createElement(Button, { positive: true, content: "Request your complimentary Starter Kit", disabled: !_this.canRequestStarterKit(), onClick: _this.onRequestStarterKit }));
        };
        _this.submit = function () {
            _this.setState({
                loading: true
            }, function () {
                var reqBody = {
                    email: _this.state.email,
                    token: _this.state.token,
                    customerName: _this.state.customerName,
                    referred: _this.state.referred,
                    uiOrigin: _this.state.uiOrigin
                };
                postJsonNoAuthn(VERIFY_EMAIL_ENDPOINT, JSON.stringify(reqBody))
                    .then(function (resp) {
                    if (resp.ok) {
                        return resp.json();
                    }
                    else {
                        return undefined;
                    }
                })
                    .then(function (resp) {
                    if (resp) {
                        _this.setState({
                            loading: false,
                            error: false,
                            success: true,
                            oldVerificationStatus: resp.oldVerificationStatus,
                            newVerificationStatus: resp.newVerificationStatus
                        });
                        return;
                    }
                    else {
                        _this.setState({
                            loading: false,
                            error: true,
                            success: false,
                            oldVerificationStatus: false,
                            newVerificationStatus: false
                        });
                        return;
                    }
                })
                    .catch(function (rej) {
                    console.error("Couldn't verify email address: " + rej);
                    _this.setState({
                        loading: false,
                        error: true,
                        success: false,
                        oldVerificationStatus: false,
                        newVerificationStatus: false,
                    });
                });
            });
        };
        _this.shouldShowStarterKitRequest = function () {
            return _this.state.success
                && !_this.state.oldVerificationStatus
                && _this.state.newVerificationStatus;
        };
        _this.shouldShowHcrAutomatedStarterKitRequest = function () {
            return _this.shouldShowStarterKitRequest() && _this.verificationOrigin() === "hcr";
        };
        _this.verificationOrigin = function () {
            return (_this.state.uiOrigin || ORIGIN || "").toLowerCase();
        };
        _this.selectedStarterKitProduct = function () {
            return _this.state.starterKitProduct;
        };
        _this.canRequestStarterKit = function () {
            return _this.validateStarterKit().length === 0
                && !_this.state.starterKitRequested
                && !_this.state.loading;
        };
        _this.canRequestAutomatedProStarterKit = function (catalog) {
            return _this.validateAutomatedProStarterKit(catalog).length === 0
                && !_this.state.starterKitRequested
                && !_this.state.loading;
        };
        _this.validateStarterKit = function () {
            var errors = [];
            if (_this.state.starterKitProduct === "") {
                errors.push({ path: "/starterKit/product", msg: "Product is required" });
            }
            if (_this.state.starterKitSampleType === "") {
                errors.push({ path: "/starterKit/sampleType", msg: "Sample type is required" });
            }
            else if (!_this.starterKitSampleTypeOptions().some(function (option) { return option.value === _this.state.starterKitSampleType; })) {
                errors.push({ path: "/starterKit/sampleType", msg: "Sample type is not available for this product" });
            }
            if (_this.selectedStarterKitProduct() === "GOLD_IF" && _this.state.starterKitEncoderSpecies === "") {
                errors.push({ path: "/starterKit/encoderSpecies", msg: "Encoding isotype is required" });
            }
            return errors;
        };
        _this.starterKitSampleTypeOptions = function () {
            return _this.selectedStarterKitProduct() === "GOLD_RNA_FISH"
                ? RNA_FISH_STARTER_KIT_SAMPLE_TYPE_OPTIONS
                : STARTER_KIT_SAMPLE_TYPE_OPTIONS;
        };
        _this.validateAutomatedProStarterKit = function (catalog) {
            var errors = [];
            var protocolType = _this.selectedAutomatedProProtocolType(catalog);
            if (protocolType === undefined) {
                errors.push({ path: "/starterKit/protocolTypeId", msg: "Platform is required" });
            }
            if (_this.selectedAutomatedProOrganism(catalog) === undefined) {
                errors.push({ path: "/starterKit/libraryOrganismId", msg: "Organism is required" });
            }
            if (protocolType !== undefined && _this.selectedAutomatedProEnzyme(catalog, protocolType) === undefined) {
                errors.push({ path: "/starterKit/enzymeCompatibilityId", msg: "Detection enzyme is required" });
            }
            return errors;
        };
        _this.starterKitRequestBody = function () {
            if (_this.state.starterKitSampleType === "") {
                return undefined;
            }
            switch (_this.selectedStarterKitProduct()) {
                case "":
                    return undefined;
                case "GOLD_RNA_FISH":
                    return {
                        _type: "rna-fish",
                        sampleType: _this.state.starterKitSampleType,
                    };
                case "PRO_RNA_ISH":
                    return {
                        _type: "manual-pro",
                        sampleType: _this.state.starterKitSampleType,
                        amplifierSystem: STARTER_KIT_DEFAULT_AMPLIFIER_SYSTEM,
                    };
                case "GOLD_IF":
                    if (_this.state.starterKitEncoderSpecies === "") {
                        return undefined;
                    }
                    return {
                        _type: "manual-gold-if",
                        sampleType: _this.state.starterKitSampleType,
                        encoderSpecies: _this.state.starterKitEncoderSpecies,
                    };
            }
        };
        _this.automatedProStarterKitRequestBody = function (catalog) {
            var protocolType = _this.selectedAutomatedProProtocolType(catalog);
            var organism = _this.selectedAutomatedProOrganism(catalog);
            var enzyme = protocolType === undefined ? undefined : _this.selectedAutomatedProEnzyme(catalog, protocolType);
            if (protocolType === undefined || organism === undefined || enzyme === undefined) {
                return undefined;
            }
            return {
                _type: "automated-pro",
                protocolTypeId: protocolType.id,
                libraryOrganismId: organism.id,
                enzymeCompatibilityId: enzyme.id,
            };
        };
        _this.hcrAutomatedStarterKitProtocolTypes = function (catalog) {
            return HCR_STARTER_KIT_PROTOCOL_TYPE_ORDER
                .map(function (protocolTypeId) { return catalog.rnaCatalog.proPlatforms.find(function (protocolType) { return protocolType.id === protocolTypeId; }); })
                .filter(function (protocolType) { return protocolType !== undefined; });
        };
        _this.hcrAutomatedStarterKitOrganisms = function (catalog) {
            return catalog.rnaCatalog.probeSet.catalog.organisms
                .filter(function (organism) { return HCR_STARTER_KIT_ORGANISM_NAMES.indexOf(organism.name) !== -1; });
        };
        _this.hcrAutomatedStarterKitEnzymes = function (catalog, protocolType) {
            var ampCatalog = catalog.rnaCatalog.proAmplifier;
            return HCR_STARTER_KIT_ENZYME_LABELS
                .map(function (label) { return ampCatalog.oneTargetProAmps.proAmpConfigList.find(function (config) {
                return config.label === label
                    && config.platformCompatibility.some(function (compatibleProtocolType) {
                        return compatibleProtocolType.id === protocolType.id;
                    });
            }); })
                .filter(function (config) { return config !== undefined; });
        };
        _this.selectedAutomatedProProtocolType = function (catalog) {
            var protocolTypes = _this.hcrAutomatedStarterKitProtocolTypes(catalog);
            return protocolTypes.find(function (protocolType) { return protocolType.id === _this.state.automatedProProtocolTypeId; })
                || protocolTypes.find(function (protocolType) { return protocolType.id === HCR_STARTER_KIT_DEFAULT_PROTOCOL_TYPE_ID; })
                || protocolTypes[0];
        };
        _this.selectedAutomatedProOrganism = function (catalog) {
            var organisms = _this.hcrAutomatedStarterKitOrganisms(catalog);
            return organisms.find(function (organism) { return organism.id === _this.state.automatedProLibraryOrganismId; })
                || organisms.find(function (organism) { return organism.name === HCR_STARTER_KIT_DEFAULT_ORGANISM_NAME; })
                || organisms[0];
        };
        _this.selectedAutomatedProEnzyme = function (catalog, protocolType) {
            var enzymes = _this.hcrAutomatedStarterKitEnzymes(catalog, protocolType);
            return enzymes.find(function (enzyme) { return enzyme.id === _this.state.automatedProEnzymeCompatibilityId; })
                || enzymes.find(function (enzyme) { return enzyme.label === HCR_STARTER_KIT_DEFAULT_ENZYME_LABEL; })
                || enzymes[0];
        };
        _this.hcrAutomatedStarterKitProtocolTypeLabel = function (protocolType) {
            switch (protocolType.id) {
                case "ROCHE":
                    return "DISCOVERY ULTRA";
                case "LEICA":
                    return "BOND RX / RXm";
                case "ONCORE_PRO_X":
                    return "ONCORE Pro X";
                case "BOND_III":
                    return "Fully ambient-temperature workflow";
                default:
                    return protocolType.alias || protocolType.label;
            }
        };
        _this.hcrAutomatedStarterKitEnzymeLabel = function (enzyme) {
            switch (enzyme.label) {
                case "AP":
                    return "Alkaline phosphatase (AP)";
                case "HRP":
                    return "Horseradish peroxidase (HRP)";
                default:
                    return enzyme.label;
            }
        };
        _this.onSelectStarterKitProduct = function (value) { return function () {
            _this.setState(function (prev) {
                if (prev.starterKitProduct === value) {
                    return null;
                }
                return {
                    starterKitProduct: value,
                    starterKitSampleType: "",
                    starterKitEncoderSpecies: "",
                    starterKitRequested: false,
                    starterKitErrors: [],
                };
            });
        }; };
        _this.onStarterKitSelectChange = function (key) {
            return function (_, data) {
                var _a;
                var val = data.value;
                if (typeof val !== "string") {
                    throw new Error("Unexpected value for " + key + ": " + val);
                }
                _this.setState((_a = {},
                    _a[key] = val,
                    _a.starterKitRequested = false,
                    _a.starterKitErrors = [],
                    _a));
            };
        };
        _this.onAutomatedProProtocolTypeChange = function (catalog) {
            return function (_, data) {
                var val = data.value;
                if (typeof val !== "string") {
                    throw new Error("Unexpected value for automatedProProtocolTypeId: " + val);
                }
                var protocolType = _this.hcrAutomatedStarterKitProtocolTypes(catalog)
                    .find(function (candidate) { return candidate.id === val; });
                var selectedEnzymeId = _this.state.automatedProEnzymeCompatibilityId;
                var selectedEnzymeStillValid = protocolType !== undefined
                    && selectedEnzymeId !== undefined
                    && _this.hcrAutomatedStarterKitEnzymes(catalog, protocolType)
                        .some(function (enzyme) { return enzyme.id === selectedEnzymeId; });
                _this.setState({
                    automatedProProtocolTypeId: val,
                    automatedProEnzymeCompatibilityId: selectedEnzymeStillValid ? selectedEnzymeId : undefined,
                    starterKitRequested: false,
                    starterKitErrors: [],
                });
            };
        };
        _this.onAutomatedProLibraryOrganismChange = function (_, data) {
            var val = data.value;
            if (typeof val !== "number") {
                throw new Error("Unexpected value for automatedProLibraryOrganismId: " + val);
            }
            _this.setState({
                automatedProLibraryOrganismId: val,
                starterKitRequested: false,
                starterKitErrors: [],
            });
        };
        _this.onAutomatedProEnzymeCompatibilityChange = function (_, data) {
            var val = data.value;
            if (typeof val !== "number") {
                throw new Error("Unexpected value for automatedProEnzymeCompatibilityId: " + val);
            }
            _this.setState({
                automatedProEnzymeCompatibilityId: val,
                starterKitRequested: false,
                starterKitErrors: [],
            });
        };
        _this.onRequestStarterKit = function () {
            var errors = _this.validateStarterKit();
            var reqBody = _this.starterKitRequestBody();
            if (errors.length > 0 || reqBody === undefined) {
                _this.setState({ starterKitErrors: errors });
                return;
            }
            _this.submitStarterKitRequest(reqBody);
        };
        _this.onRequestAutomatedProStarterKit = function (catalog) { return function () {
            var errors = _this.validateAutomatedProStarterKit(catalog);
            var reqBody = _this.automatedProStarterKitRequestBody(catalog);
            if (errors.length > 0 || reqBody === undefined) {
                _this.setState({ starterKitErrors: errors });
                return;
            }
            _this.submitStarterKitRequest(reqBody);
        }; };
        _this.submitStarterKitRequest = function (reqBody) {
            _this.setState({ loading: true, starterKitErrors: [] }, function () {
                postJsonWithAuthn(CREATE_STARTER_KIT_ENDPOINT, JSON.stringify(reqBody))
                    .then(function (resp) {
                    if (resp.ok) {
                        _this.setState({ loading: false, starterKitRequested: true }, function () {
                            _this.props.history.push(AppPaths.CART);
                        });
                        return;
                    }
                    else if (resp.status === 400) {
                        return resp.json();
                    }
                    else {
                        _this.setState({
                            loading: false,
                            starterKitErrors: [{ path: "/", msg: "An unexpected error occurred" }],
                        });
                        return;
                    }
                })
                    .then(function (resp) {
                    if (resp !== undefined) {
                        _this.setState({ loading: false, starterKitErrors: resp.errors });
                    }
                })
                    .catch(function (rej) {
                    console.error("Couldn't request Starter Kit: " + rej);
                    _this.setState({
                        loading: false,
                        starterKitErrors: [{ path: "/", msg: "An unexpected error occurred" }],
                    });
                });
            });
        };
        var search = parse(props.history.location.search);
        var referred;
        if ('referred' in search && typeof search.referred === 'string') {
            referred = search.referred === "true";
        }
        else {
            referred = false;
        }
        _this.state = {
            loading: false,
            error: false,
            success: false,
            email: ('email' in search && typeof search.email === 'string') ? search.email : '',
            token: ('token' in search && typeof search.token === 'string') ? search.token : '',
            customerName: ('customerName' in search && typeof search.customerName === 'string') ? search.customerName : '',
            referred: referred,
            uiOrigin: ('uiOrigin' in search && typeof search.uiOrigin === 'string') ? search.uiOrigin : '',
            oldVerificationStatus: false,
            newVerificationStatus: false,
            starterKitProduct: "",
            starterKitSampleType: "",
            starterKitEncoderSpecies: "",
            automatedProProtocolTypeId: HCR_STARTER_KIT_DEFAULT_PROTOCOL_TYPE_ID,
            automatedProLibraryOrganismId: undefined,
            automatedProEnzymeCompatibilityId: undefined,
            starterKitRequested: false,
            starterKitErrors: [],
        };
        return _this;
    }
    VerifyEmailPage.prototype.render = function () {
        var showStarterKitRequest = this.shouldShowStarterKitRequest();
        var showHcrAutomatedStarterKitRequest = this.shouldShowHcrAutomatedStarterKitRequest();
        var rootClassNames = showStarterKitRequest
            ? ["new-registration-page", "starter-kit-registration-page", "verify-email-page"]
            : ["verify-email-page"];
        if (showHcrAutomatedStarterKitRequest || (showStarterKitRequest && ORIGIN === "hcr")) {
            rootClassNames.push("hcr-registration-page");
        }
        if (showHcrAutomatedStarterKitRequest) {
            rootClassNames.push("hcr-automated-starter-kit-page");
        }
        var rootClassName = rootClassNames.join(" ");
        return React.createElement("div", { id: "non-header-portion", className: rootClassName },
            showStarterKitRequest &&
                React.createElement(Header, { as: "h2", className: "new-registration-title" }, showHcrAutomatedStarterKitRequest
                    ? "Starter Kit preview"
                    : "Request your complimentary Starter Kit"),
            React.createElement(Dimmer.Dimmable, { as: "div", className: showStarterKitRequest ? "new-registration-panel" : undefined },
                React.createElement(Dimmer, { active: this.state.loading, inverted: true },
                    React.createElement(Loader, null)),
                this.state.error
                    && React.createElement(Message, { error: true },
                        React.createElement(Message.Header, null, "Verification failed"),
                        "Please check that the email address and token are correct."),
                this.state.success
                    && this.renderSuccessMessage(),
                showStarterKitRequest && (showHcrAutomatedStarterKitRequest
                    ? this.renderHcrAutomatedStarterKitRequest()
                    : this.renderStarterKitStep())),
            showStarterKitRequest && !showHcrAutomatedStarterKitRequest && this.renderStarterKitActions());
    };
    VerifyEmailPage.prototype.componentDidMount = function () {
        this.submit();
    };
    return VerifyEmailPage;
}(React.PureComponent));
export default VerifyEmailPage;
//# sourceMappingURL=VerifyEmailPage.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/VerifyEmailPage.js
// module id = 978
// module chunks = 0